import { useLocation, useNavigate } from "react-router-dom";
import { TAB_PATH } from "@/lib/tabPaths";
import {
  ShoppingCart, Receipt, Landmark, PiggyBank, ClipboardCheck,
  LayoutDashboard, Funnel, FileText, Scale, Building2, ClipboardList, FileSignature,
  Calculator, Tags, FolderOpen, Settings, ScanSearch, PackageCheck, FolderKanban, Inbox,
  CalendarDays, Truck, Map as MapIcon, Wrench, PenLine, Warehouse, Clock, Users, Boxes,
} from "lucide-react";
import { Menu, DraftingCompass } from "lucide-react";
import SyncIndicator from "@/fieldops/components/SyncIndicator";
import MentionsBell from "@/components/MentionsBell";
import { NavHistoryControls } from "@/fieldops/context/NavHistoryContext";

// One title per tab, icons matching the left rail exactly. Row height matches the sidebar's
// logo block so the title sits on the same line as the ABS mark.
const PAGES = {
  dashboard: { label: "Dashboard", icon: LayoutDashboard },
  pipeline: { label: "Pipeline", icon: Funnel },
  bod: { label: "Basis of Design", icon: DraftingCompass },
  contracts: { label: "Contracts", icon: FileText },
  contractadmin: { label: "Contract Admin", icon: ScanSearch },
  reconcile: { label: "Reconcile", icon: Scale },
  accounts: { label: "Accounts", icon: Building2 },
  bidboard: { label: "Bid Board", icon: ClipboardList },
  itbinbox: { label: "Inbox", icon: Inbox },
  proposals: { label: "Estimate", icon: FileSignature },
  budget: { label: "Budgets", icon: Calculator },
  pricelist: { label: "Cost Library", icon: Tags },
  doclibrary: { label: "Doc Library", icon: FolderOpen },
  opsdashboard: { label: "Operations", icon: LayoutDashboard },
  opsprojects: { label: "Jobs", icon: FolderKanban },
  purchasing: { label: "Purchasing", icon: ShoppingCart },
  billing: { label: "Billing", icon: Receipt },
  ar: { label: "Receivables", icon: Landmark },
  retention: { label: "Retention", icon: PiggyBank },
  closeout: { label: "Closeout", icon: ClipboardCheck },
  subbuilder: { label: "SubBuilder", icon: PackageCheck },
  settings: { label: "Settings", icon: Settings },
};

// Field Operations owns real URLs, so its title comes from the path. Longest prefix wins.
const FIELD_PAGES = [
  ["/install/onsite", { label: "Onsite Material", icon: Boxes }],
  ["/dashboard", { label: "Field Operations", icon: LayoutDashboard }],
  ["/projects", { label: "Resource Allocation", icon: FolderOpen }],
  ["/schedule", { label: "Schedule", icon: CalendarDays }],
  ["/dispatch", { label: "Dispatch", icon: Truck }],
  ["/installers", { label: "Installers", icon: Users }],
  ["/map", { label: "Map", icon: MapIcon }],
  ["/install", { label: "Install", icon: Wrench }],
  ["/drawings", { label: "Drawings", icon: PenLine }],
  ["/warehouse", { label: "Warehouse", icon: Warehouse }],
  ["/timeclock", { label: "Time Clock", icon: Clock }],
  ["/timesheets", { label: "Timesheets", icon: ClipboardCheck }],
];

export default function PageHeader({ tab, onOpenNav }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const field = tab === "fieldops";
  const fieldHit = field ? FIELD_PAGES.find(([p]) => pathname === p || pathname.startsWith(`${p}/`)) : null;
  const page = field ? (fieldHit || [])[1] : PAGES[tab];
  if (!page) return null;
  const Icon = page.icon;
  // The module title is the way back to that module's landing page — same look, just pressable.
  const home = field ? (fieldHit ? fieldHit[0] : "/dashboard") : TAB_PATH[tab];
  return (
    <header className="flex h-16 shrink-0 items-center gap-2.5 border-b border-[#262626] px-4"
            style={{ backgroundColor: "#141414" }} data-testid="page-header">
      <button type="button" onClick={onOpenNav} data-testid="open-nav-btn"
              className="-ml-1 mr-0.5 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-white md:hidden">
        <Menu className="h-5 w-5" />
      </button>
      <NavHistoryControls className="-ml-1 md:hidden" />
      <button type="button" onClick={() => home && navigate(home)} data-testid="page-title-link" title={`Back to ${page.label}`}
              className="flex min-w-0 items-center gap-2.5 rounded-md text-left transition-opacity hover:opacity-80">
        <Icon className="h-5 w-5 shrink-0 text-slate-400" />
        <h1 className="min-w-0 truncate text-lg sm:text-xl font-semibold tracking-tight text-white"
            style={{ fontFamily: "Cabinet Grotesk, sans-serif" }} data-testid="page-title">
          {page.label}
        </h1>
      </button>
      <div id="page-header-slot" className="ml-3 hidden min-w-0 items-center md:flex" data-testid="page-header-slot" />
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {field && (
          <div className="fo-scope flex shrink-0 items-center bg-transparent md:hidden" data-testid="page-header-aside">
            <SyncIndicator />
          </div>
        )}
        <MentionsBell />
      </div>
    </header>
  );
}
